import { DeleteOutlined } from '@ant-design/icons';
import { Modal, Table, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LifeReviewResponse } from 'api/LifeReviewApi';
import { useGetAllLifeReviewResponsesSysAdminQuery, useRemoveSysAdminLifeReviewResponseMutation } from 'api/SysAdminApi';
import { shouldSkipSysAdminLifeReviewQuery } from 'components/SysAdmin/sysAdminIdentityGuards';
import { useState } from 'react';

type AllProps = {
  userId?: string;
  patientProfileId?: string;
};

const PAGE_SIZE = 10;

function SysAdminLifeReviewResponseListComponent({ userId, patientProfileId }: AllProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [removeResponse, { isLoading: isRemoving }] = useRemoveSysAdminLifeReviewResponseMutation();
  const [messageApi, contextHolder] = message.useMessage();

  const { data, isFetching } = useGetAllLifeReviewResponsesSysAdminQuery(
    {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
      ...(userId ? { userId } : {}),
      ...(patientProfileId ? { patientProfileId } : {})
    },
    { skip: shouldSkipSysAdminLifeReviewQuery(userId, patientProfileId) }
  );

  const onTableChanged = (pagination) => {
    setCurrentPage(pagination.current || 1);
  };

  const handleDelete = (response: LifeReviewResponse) => {
    Modal.confirm({
      title: 'Delete life review response',
      content: 'Are you sure you want to delete this life review response? This action may remove patient-created content.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!response.id) {
          messageApi.error('Unable to delete life review response: missing response id.');
          return;
        }

        try {
          await removeResponse({ responseId: response.id }).unwrap();
          messageApi.success('Life review response deleted successfully.');
        } catch (e) {
          messageApi.error('Failed to delete life review response. Please try again.');
        }
      }
    });
  };

  const columns: ColumnsType<LifeReviewResponse> = [
    {
      title: 'Prompt',
      key: 'prompt',
      render: (_, response) => response.promptText || response.prompt || response.question || 'N/A'
    },
    {
      title: 'Response',
      key: 'responseText',
      ellipsis: true,
      render: (_, response) => response.editedTranscriptText || response.responseText || response.text || response.transcriptText || 'N/A'
    },
    {
      title: 'Created',
      key: 'createdAt',
      render: (_, response) => response.createdAt || 'N/A'
    },
    {
      render: (_, response) => (
        <Tooltip title="Delete Life Review Response">
          <button
            type="button"
            aria-label="Delete life review response"
            onClick={() => handleDelete(response)}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      )
    }
  ];

  return (
    <>
      {contextHolder}
      <div className="sys-admin-user-list-component">
        <h3>Life Review Responses ({data?.total || 0})</h3>
        <Table
          columns={columns}
          dataSource={data?.list || []}
          pagination={{
            defaultPageSize: PAGE_SIZE,
            current: currentPage,
            total: data?.total || 0
          }}
          onChange={onTableChanged}
          rowKey={(response) => response.id || `${response.promptId || 'prompt'}-${response.createdAt || 'unknown'}`}
          bordered
          loading={isFetching || isRemoving}
          locale={{
            emptyText: <Typography.Text>No data</Typography.Text>
          }}
        />
      </div>
    </>
  );
}

export default SysAdminLifeReviewResponseListComponent;